import React, { useState, useEffect } from 'react';
import { ArrowRight, Search, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Profile } from '../lib/supabase';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { Tooltip } from '../components/Tooltip';
import { localCustomerDB } from '../lib/localDB';

interface CustomerWithBalance extends Profile {
  balance?: number;
  avatarUrl?: string;
}

function AddTransaction() {
  const [amount, setAmount] = useState('0');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerWithBalance | null>(null);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [customers, setCustomers] = useState<CustomerWithBalance[]>([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const navigate = useNavigate();


  useEffect(() => {
    fetchUserProfile();
  }, []);

   // Refresh data when page comes back into focus
   useEffect(() => {
     const handleFocus = () => {
       if (userProfile?.smart_key) {
         fetchAllCustomers();
       }
     };

     window.addEventListener('focus', handleFocus);
     
     return () => {
       window.removeEventListener('focus', handleFocus);
     };
   }, [userProfile?.smart_key]);

  useEffect(() => {
    if (userProfile?.smart_key) {
      fetchAllCustomers();
    }
  }, [userProfile?.smart_key]);

  const fetchUserProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profile) {
        setUserProfile(profile);
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  };

  const fetchAllCustomers = async () => {
    try {
      if (!userProfile?.smart_key) return;

      setCustomersLoading(true);
      let vendorSmartKey: string;

      if (userProfile.account_type === 'shop_owner') {
        vendorSmartKey = userProfile.smart_key;
      } else if (userProfile.account_type === 'shop_assistant') {
        // Get vendor's smart key through assistant relationship
        const { data: assistantData } = await supabase
          .rpc('get_vendor_for_assistant');
        vendorSmartKey = assistantData;
      } else {
        throw new Error('Unauthorized access');
      }

      if (!vendorSmartKey) {
        throw new Error('Could not determine vendor');
      }

      // Try to load from cache first
      try {
        console.log('Checking cache for customers...');
        const cachedCustomers = await localCustomerDB.getCachedCustomers(vendorSmartKey);
        
        if (cachedCustomers.length > 0) {
          console.log('Found cached customers:', cachedCustomers.length);
          
          // Convert cached data to expected format
          const customersFromCache = cachedCustomers.map(customer => ({
            ...customer,
            id: customer.smart_key,
            phone: '',
            country_code: '',
            date_of_birth: '',
            address: '',
            account_type: 'customer' as const,
            reference_smart_key: null,
            shop_name: null,
            business_type: null,
            created_at: '',
            updated_at: '',
            assistant_vendor_smart_key: null,
            assistant_status: null,
            recovery_email: null,
            balance: customer.balance || 0,
            avatarUrl: customer.avatar_url ? supabase.storage.from('avatars').getPublicUrl(customer.avatar_url).data.publicUrl : null
          }));
          
          setCustomers(customersFromCache);
          setCustomersLoading(false);
          
          // Check if cache needs refresh in background
          const isStale = await localCustomerDB.isCacheStale(vendorSmartKey);
          if (isStale) {
            console.log('Cache is stale, refreshing in background...');
            // Refresh in background without affecting UI
            fetchCustomersFromServer(vendorSmartKey, true);
          }
          
          return;
        }
      } catch (cacheError) {
        console.warn('Cache read failed, falling back to server:', cacheError);
      }

      // If no cache or cache failed, fetch from server
      await fetchCustomersFromServer(vendorSmartKey, false);

    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('فشل في تحميل العملاء');
    } finally {
      setCustomersLoading(false);
    }
  };

  const fetchCustomersFromServer = async (vendorSmartKey: string, isBackgroundRefresh: boolean) => {
    try {
      console.log('Fetching customers from server...');
      
      // Get all customers linked to this vendor
      const { data: relationships, error: relationshipsError } = await supabase
        .from('vendor_customer_relationships')
        .select('customer_smart_key')
        .eq('vendor_smart_key', vendorSmartKey);

      if (relationshipsError) throw relationshipsError;

      if (!relationships?.length) {
        setCustomers([]);
        return;
      }

      const customerSmartKeys = relationships.map(r => r.customer_smart_key);

      // Get profiles for all customers
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .in('smart_key', customerSmartKeys);

      if (profilesError) throw profilesError;

      // Fetch balance and avatar URL for each customer
      const customersWithDetails = await Promise.all(
        (profiles || []).map(async (customer) => {
         // Fetch balance only between current vendor and this customer
          const { data: transactions } = await supabase
            .from('transactions')
            .select('amount')
           .eq('vendor_smart_key', vendorSmartKey)
            .eq('customer_smart_key', customer.smart_key);

          const balance = transactions?.reduce((sum, tx) => sum + (tx.amount || 0), 0) || 0;

          // Get avatar URL if available
          let avatarUrl = customer.avatar_url;
          if (customer.avatar_url) {
            const { data: { publicUrl } } = supabase
              .storage
              .from('avatars')
              .getPublicUrl(customer.avatar_url);
            avatarUrl = publicUrl;
          }

          return {
            ...customer,
            balance,
            avatarUrl
          };
        })
      );

      // Update cache with fresh data
      try {
        const cacheData = customersWithDetails.map(customer => ({
          smart_key: customer.smart_key,
          username: customer.username,
          currency: customer.currency,
          avatar_url: customer.avatarUrl, // Store the full public URL in cache
          balance: customer.balance || 0,
          last_updated: Date.now()
        }));
        
        await localCustomerDB.cacheCustomers(vendorSmartKey, cacheData);
        console.log('Customers cached successfully');
      } catch (cacheError) {
        console.warn('Failed to cache customers:', cacheError);
      }
      
      // Only update UI if not a background refresh
      if (!isBackgroundRefresh) {
        setCustomers(customersWithDetails);
      } else {
        // For background refresh, update the cache and then update the UI with fresh data
        const customersFromUpdatedCache = cachedCustomers.map(customer => ({
          ...customer,
          id: customer.smart_key,
          phone: '',
          country_code: '',
          date_of_birth: '',
          address: '',
          account_type: 'customer' as const,
          reference_smart_key: null,
          shop_name: null,
          business_type: null,
          created_at: '',
          updated_at: '',
          assistant_vendor_smart_key: null,
          assistant_status: null,
          recovery_email: null,
          balance: customer.balance || 0,
          avatarUrl: customer.avatar_url
        }));
        setCustomers(customersFromUpdatedCache);
      }
      
    } catch (err) {
      console.error('Error fetching customers from server:', err);
      if (!isBackgroundRefresh) {
        setError('فشل في تحميل العملاء');
      }
    }
  };

  const handleNumberClick = (num: string) => {
    if (num === 'backspace') {
      setAmount(prev => prev.replace(/,/g, '').slice(0, -1).replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '0');
    } else if (num === '.') {
      if (!amount.includes('.')) {
        setAmount(prev => prev + '.');
      }
    } else {
      setAmount(prev => 
        (prev === '0' ? num : prev + num)
      );
    }
  };

  const handleCustomerSelect = (customer: CustomerWithBalance) => {
    setSelectedCustomer(customer);
  };

  const handleSubmit = async () => {
    if (!selectedCustomer || loading) return;

    setLoading(true);
    setError(null);

    try {
      // Get vendor's smart key (either directly or through assistant relationship)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: userProfile } = await supabase
        .from('profiles')
        .select('smart_key, account_type')
        .eq('id', user.id)
        .single();

      if (!userProfile) throw new Error('Profile not found');

      let vendorSmartKey: string;

      if (userProfile.account_type === 'shop_owner') {
        vendorSmartKey = userProfile.smart_key;
      } else if (userProfile.account_type === 'shop_assistant') {
        // Get vendor's smart key through assistant relationship
        const { data: assistantData } = await supabase
          .rpc('get_vendor_for_assistant');
        
        vendorSmartKey = assistantData;
      } else {
        throw new Error('Unauthorized access');
      }

      if (!vendorSmartKey) {
        throw new Error('Could not determine vendor');
      }

      // Parse amount and ensure it's positive
      const parsedAmount = Math.abs(parseFloat(amount.replace(/,/g, '')));
      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        throw new Error('Invalid amount');
      }

      // Create the transaction (always debit for AddTransaction)
      const { error: transactionError } = await supabase
        .from('transactions')
        .insert({
          vendor_smart_key: vendorSmartKey,
          customer_smart_key: selectedCustomer.smart_key,
          amount: -parsedAmount, // Negative amount for debit
          currency: selectedCustomer.currency,
          payment_type: 'debit',
          remarks: remarks.trim()
        });

      if (transactionError) {
        console.error('Transaction error:', transactionError);
        throw transactionError;
      }

      // Navigate back to home
      navigate('/home');
    } catch (err) {
      console.error('Error creating transaction:', err);
      setError(err instanceof Error ? err.message : 'Failed to create transaction');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white px-6 pt-8">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-8">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowRight size={24} className="text-gray-800" />
        </button>
        <div className="flex items-center space-x-2">
          <h1 className="text-xl font-semibold">إضافة عملية</h1>
          <Tooltip text="إضافة عملية جديدة للعميل. يمكنك تحديد المبلغ والعميل وإضافة ملاحظات للعملية." />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 rounded-xl text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Customer Selection */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-semibold">اختر العميل</h2>
            <Tooltip text="اختر العميل من قائمة عملائك المسجلين" />
          </div>
          {selectedCustomer && (
            <button 
              onClick={() => {
                setSelectedCustomer(null);
                setRemarks('');
              }}
              className="text-blue-500 hover:text-blue-600 text-sm"
            >
              تغيير العميل
            </button>
          )}
        </div>

        {customersLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : customers.length === 0 ? (
          <div className="flex items-start space-x-4 py-6">
            {/* Add New Customer Button */}
            <div className="flex-none flex flex-col items-center space-y-2">
              <button 
                className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center hover:opacity-90 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                onClick={() => navigate('/add-customer')}
              >
                <Plus size={28} className="text-white font-bold drop-shadow-sm" />
              </button>
              <p className="text-xs text-gray-700 font-medium text-center leading-tight">تسجيل عميل<br/>جديد</p>
            </div>
            
            {/* Informative Message */}
            <div className="flex-1 bg-blue-50 rounded-xl p-4 border border-blue-200">
              <p className="text-sm text-blue-800 leading-relaxed">
                لا يوجد لديك أي عملاء مسجلين حالياً، قم بتسجيل عملائك الذين تود التعامل معهم، لا تحتاج لتسجيل نفس العميل أكثر من مرة
              </p>
            </div>
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory hide-scrollbar">
            {/* Add New Customer Button */}
            <div className="flex-none flex flex-col items-center space-y-2">
              <button 
                className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center hover:opacity-90 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                onClick={() => navigate('/add-customer')}
              >
                <Plus size={28} className="text-white font-bold drop-shadow-sm" />
              </button>
              <p className="text-xs text-gray-700 font-medium text-center leading-tight">تسجيل عميل<br/>جديد</p>
            </div>

            {customers.map(customer => (
              <div 
                key={customer.smart_key} 
                className="flex-none flex flex-col items-center space-y-2"
              >
                <div className={`relative ${selectedCustomer?.smart_key === customer.smart_key ? 'ring-4 ring-blue-300 rounded-full' : ''}`}>
                  <ProfileAvatar
                    src={customer.avatarUrl}
                    username={customer.username}
                    size="lg"
                    onClick={() => handleCustomerSelect(customer)}
                  />
                  {selectedCustomer?.smart_key === customer.smart_key && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-600 font-medium">{customer.username.split(' ')[0]}</p>
                  <p className="text-xs text-gray-500">
                    {customer.balance?.toFixed(2)} {customer.currency}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Amount */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-baseline">
          <span className="text-4xl font-semibold">{amount}</span>
        </div>
        <button className="px-3 py-1.5 rounded-lg bg-gray-100 text-sm font-medium flex items-center space-x-1">
          <span>{selectedCustomer?.currency || 'ريال'}</span>
        </button>
      </div>

      {/* Selected Customer */}
      {selectedCustomer && (
        <div className="w-full bg-gray-50 rounded-xl p-4 space-y-4 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-medium">
                  {selectedCustomer.username.substring(0, 2).toUpperCase()}
                </span>
              </div>
              <div>
                <span className="font-medium">{selectedCustomer.username}</span>
                <p className="text-sm text-gray-500">
                  الرصيد: {selectedCustomer.balance?.toFixed(2)} {selectedCustomer.currency}
                </p>
              </div>
            </div>
            <button 
              onClick={() => {
                setSelectedCustomer(null);
                setSearchQuery('');
                setRemarks('');
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              تغيير
            </button>
          </div>
          
          {/* Remarks Field */}
          <div className="relative">
            <input
              type="text"
              placeholder="إضافة ملاحظات..."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 bg-white"
            />
          </div>
        </div>
      )}

      {/* Numpad */}
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, '.', 0, 'backspace'].map((num) => (
          <button
            key={num}
            onClick={() => handleNumberClick(num.toString())}
            className="h-14 rounded-2xl bg-gray-50 font-semibold text-lg flex items-center justify-center hover:bg-gray-100 transition-colors"
          >
            {num === 'backspace' ? '⌫' : num}
          </button>
        ))}
      </div>

      {/* Add Transaction Button */}
      <button 
        onClick={handleSubmit}
        disabled={!selectedCustomer || loading || amount === '0'}
        className="w-full bg-red-500 text-white rounded-xl py-4 font-medium mt-8 hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'جاري المعالجة...' : 'إضافة عملية'}
      </button>
    </div>
  );
}

export default AddTransaction;